// Copyright (c) 2024 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.

import React, { useState, useEffect, useRef } from 'react';
import {
    isValidCashtabSettings,
    isValidCashtabCache,
    isValidContactList,
    migrateLegacyCashtabSettings,
} from 'validation';
import { storage, initializeStorage } from 'platform';
import {
    getTransactionHistory,
    parseTx,
    getTokenGenesisInfo,
    getTxNotificationMsg,
    ParsedTokenTxType,
} from 'chronik';
import appConfig from 'config/app';
import { chronik as chronikConfig } from 'config/chronik';
import { CashReceivedNotificationIcon } from 'components/Common/CustomIcons';
import CashtabSettings, {
    supportedFiatCurrencies,
} from 'config/CashtabSettings';
import {
    storedCashtabCacheToMap,
    CashtabCacheJson,
    cashtabCacheToJSON,
} from 'helpers';
import {
    StoredCashtabWallet,
    generateTokensFromWalletUtxos,
    CashtabTx,
    isStoredHdWallet,
} from 'wallet';
import {
    LEGACY_WALLETS_STORAGE_KEY,
    WALLET_RECORD_PREFIX,
    deleteWalletRecord,
    migrateLegacyWallets,
    persistHdIndexes,
    readWalletRecords,
    resolveActiveWalletAddress,
    saveWalletRecord,
} from 'wallet/storage';
import {
    getWalletAddressesForSubscription,
    getWalletHashesForParseTx,
} from 'wallet/hd';
import { toast } from 'react-toastify';
import CashtabState, { CashtabContact } from 'config/CashtabState';
import { TokenIconToast } from 'components/Etokens/TokenIcon';
import { shouldSuppressInstantRedeemSaleToast } from 'components/Etokens/pendingRedeems';
import {
    consumeAlpSwapSettleTxid,
    isAlpSwapBuyerToastSuppressed,
} from 'components/AlpSwap/rememberSettleTxid';
import { getUserLocale } from 'helpers';
import { FIRMA_APY_API_URL, XECX_APY_API_URL } from 'constants/tokens';
import {
    ChronikClient,
    WsEndpoint,
    WsMsgClient,
    MsgTxClient,
} from 'chronik-client';
import { Agora } from 'ecash-agora';
import { Address, Ecc, fromHex } from 'ecash-lib';
import { Wallet } from 'ecash-wallet';

import CashtabCache from 'config/CashtabCache';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

/**
 * We keep the first page of tx history in context
 * This allows us to update it piecemeal without full
 * chronik calls by using websocket handlers, and keep
 * tx history (which is important for user info not
 * wallet actions) separate from the wallet state
 */
export interface TransactionHistory {
    firstPageTxs: CashtabTx[];
    numPages: number;
    numTxs: number;
}

export type UpdateCashtabState = (updates: {
    [key: string]:
        | CashtabCache
        | CashtabContact[]
        | CashtabSettings
        | CashtabCacheJson
        | Map<string, string>
        | string
        | null;
}) => Promise<boolean>;

export interface UseWalletReturnType {
    chronik: ChronikClient;
    agora: Agora;
    ecc: Ecc;
    fiatPrice: number | null;
    firmaPrice: number | null;
    /** Average Firma APY from FIRMA_APY_API_URL, or null. */
    firmaApy: number | null;
    /** XECX APY percent from XECX_APY_API_URL (100 * fraction), or null. */
    xecxApy: number | null;
    cashtabLoaded: boolean;
    loading: boolean;
    initialUtxoSyncComplete: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    apiError: boolean;
    updateCashtabState: UpdateCashtabState;
    processChronikWsMsg: (msg: WsMsgClient) => Promise<boolean>;
    cashtabState: CashtabState;
    transactionHistory: TransactionHistory | null;
    refreshTransactionHistory: () => Promise<void>;
    /**
     * Sync wallet data by fetching latest UTXOs and token balances
     */
    update: () => Promise<void>;
    /**
     * In some cases, we only want to set CashtabState as setting the state
     * will trigger storage writing, and we want to minimize this
     */
    setCashtabState: React.Dispatch<React.SetStateAction<CashtabState>>;
    /**
     * Wallet instance from ecash-wallet, initialized from active wallet sk
     * This is the Wallet instance from ecash-wallet
     * Wallet name is stored in cashtabState.wallets (StoredCashtabWallet)
     */
    ecashWallet: Wallet | null;
    /**
     * Get a StoredCashtabWallet by its address
     * @param address The wallet address to look up
     * @returns The StoredCashtabWallet if found, null otherwise
     */
    getWalletByAddress: (address: string) => StoredCashtabWallet | null;
    /**
     * Persist one wallet record and refresh the in-memory list from storage.
     * Pass activeAddress to also record which wallet is open.
     */
    saveWallet: (
        wallet: StoredCashtabWallet,
        activeAddress?: string,
    ) => Promise<void>;
    /**
     * Remove one wallet record. If it was active, open the first remaining
     * wallet by name.
     */
    deleteWallet: (address: string) => Promise<void>;
}

const useWallet = (chronik: ChronikClient, agora: Agora, ecc: Ecc) => {
    const [cashtabLoaded, setCashtabLoaded] = useState<boolean>(false);
    const [ws, setWs] = useState<null | WsEndpoint>(null);
    const [fiatPrice, setFiatPrice] = useState<null | number>(null);
    const [firmaPrice, setFirmaPrice] = useState<null | number>(null);
    const [firmaApy, setFirmaApy] = useState<null | number>(null);
    const [xecxApy, setXecxApy] = useState<null | number>(null);
    const [apiError, setApiError] = useState<boolean>(false);
    const [checkFiatInterval, setCheckFiatInterval] =
        useState<null | NodeJS.Timeout>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [initialUtxoSyncComplete, setInitialUtxoSyncComplete] =
        useState<boolean>(false);
    const [cashtabState, setCashtabState] = useState<CashtabState>(
        new CashtabState(),
    );
    const [transactionHistory, setTransactionHistory] =
        useState<TransactionHistory | null>(null);
    const [ecashWallet, setEcashWallet] = useState<Wallet | null>(null);
    const locale = getUserLocale();

    const refreshTransactionHistory = async () => {
        if (!ecashWalletRef.current) {
            setTransactionHistory(null);
            return;
        }

        try {
            const addresses = getWalletAddressesForSubscription(
                ecashWalletRef.current,
            );
            // NB this gives us page 0 as we call without specifying page number
            const result = await getTransactionHistory(
                chronik,
                addresses,
                currentCashtabStateRef.current.cashtabCache.tokens,
            );

            // Chronik.history() returns one page of txs plus the wallet's total
            // tx count (numTxs). We must store that total: websocket handlers
            // later do prev.numTxs +/- 1 to recompute numPages. Storing
            // result.txs.length (page size, e.g. 10) made pagination think a
            // 250-tx wallet only had ~1 page of history.
            const newTransactionHistory: TransactionHistory = {
                firstPageTxs: result.txs,
                numPages: result.numPages,
                numTxs: result.numTxs,
            };

            setTransactionHistory(newTransactionHistory);
        } catch (err) {
            console.error('Error refreshing transaction history:', err);
            setTransactionHistory(null);
        }
    };

    // Ref https://stackoverflow.com/questions/53446020/how-to-compare-oldvalues-and-newvalues-on-react-hooks-useeffect
    // Get the previous value of a state variable
    const usePrevious = <T>(value: T | undefined): T | undefined => {
        const ref = useRef<T | undefined>(undefined);
        useEffect(() => {
            ref.current = value;
        }, [value]);
        return ref.current;
    };

    const prevFiatPrice = usePrevious(fiatPrice);
    const prevFiatCurrency = usePrevious(cashtabState.settings.fiatCurrency);

    // Refs to always hold current state
    const currentCashtabStateRef = useRef<CashtabState>(cashtabState);
    const currentFiatPriceRef = useRef<number | null>(fiatPrice);
    const currentCashtabLoadedRef = useRef<boolean>(cashtabLoaded);
    // Use ref to store current websocket to avoid closure issues in app lifecycle handlers
    const wsRef = useRef<WsEndpoint | null>(null);
    // Track app state to prevent reconnection attempts when backgrounded
    const isAppActiveRef = useRef<boolean>(true);
    // Store Wallet instance from ecash-wallet (for internal use in hook)
    const ecashWalletRef = useRef<Wallet | null>(null);
    // Serialize wallet record writes in this tab so a refresh cannot drop
    // a wallet this tab just saved.
    const walletWriteQueue = useRef<Promise<void>>(Promise.resolve());
    const enqueueWalletWrite = <T>(job: () => Promise<T>): Promise<T> => {
        const run = walletWriteQueue.current.then(job, job);
        walletWriteQueue.current = run.then(
            () => undefined,
            () => undefined,
        );
        return run;
    };

    // Update refs whenever state changes
    useEffect(() => {
        currentCashtabStateRef.current = cashtabState;
    }, [cashtabState]);

    useEffect(() => {
        currentFiatPriceRef.current = fiatPrice;
    }, [fiatPrice]);

    useEffect(() => {
        currentCashtabLoadedRef.current = cashtabLoaded;
    }, [cashtabLoaded]);

    useEffect(() => {
        ecashWalletRef.current = ecashWallet;
    }, [ecashWallet]);

    // Update wsRef whenever ws changes
    useEffect(() => {
        wsRef.current = ws;
    }, [ws]);

    // Refresh transaction history when active wallet changes
    useEffect(() => {
        if (cashtabLoaded && ecashWallet) {
            refreshTransactionHistory();
        } else {
            setTransactionHistory(null);
        }
    }, [ecashWallet?.address, cashtabLoaded]);

    // Queue for processing messages in order
    const messageQueue = useRef<Array<WsMsgClient>>([]);
    const isProcessing = useRef<boolean>(false);
    /**
     * Chronik emits TX_ADDED_TO_MEMPOOL once per subscribed script involved in
     * a tx. HD wallets subscribe to many addresses, so one consolidation can
     * enqueue the same txid N times — dedupe so we notify / append history once.
     */
    const processedMempoolTxids = useRef<Set<string>>(new Set());

    // Drop processed-txid cache when the active wallet identity changes
    useEffect(() => {
        processedMempoolTxids.current.clear();
    }, [ecashWallet?.address]);

    // Process messages sequentially
    const processMessageQueue = async () => {
        if (isProcessing.current) {
            return;
        }

        isProcessing.current = true;

        while (messageQueue.current.length > 0) {
            const msg = messageQueue.current.shift()!;

            try {
                await processMessage(
                    msg,
                    currentCashtabStateRef.current,
                    currentFiatPriceRef.current,
                );
            } catch (error) {
                console.error('Error processing message:', error);
            }
        }

        isProcessing.current = false;
    };

    // Process individual message
    const processMessage = async (
        msg: WsMsgClient,
        cashtabState: CashtabState,
        fiatPrice: number | null,
    ) => {
        if (!('msgType' in msg)) {
            // No processing chronik error msgs
            console.error(`Error from chronik websocket`, msg);
            return;
        }

        const { msgType } = msg;
        const { settings, cashtabCache } = cashtabState;

        switch (msgType) {
            case 'TX_ADDED_TO_MEMPOOL': {
                // We parse txs that are added to the mempool for notifications
                const txid = (msg as MsgTxClient).txid;

                // One Chronik msg per subscribed script; process each txid once
                if (processedMempoolTxids.current.has(txid)) {
                    return;
                }
                processedMempoolTxids.current.add(txid);
                // Bound memory if finalize/remove msgs are missed. Prefer clearing
                // on TX_FINALIZED / TX_REMOVED_FROM_MEMPOOL so in-flight size stays small.
                if (processedMempoolTxids.current.size > 1000) {
                    const oldest = processedMempoolTxids.current
                        .values()
                        .next().value;
                    if (typeof oldest === 'string') {
                        processedMempoolTxids.current.delete(oldest);
                    }
                }

                // Update wallet utxo set when we see a new tx
                await update();

                let incomingTxDetails;
                try {
                    incomingTxDetails = await chronik.tx(txid);
                } catch (err) {
                    // In this case, no notification
                    return console.error(
                        `Error in chronik.tx(${txid} while processing an incoming websocket tx`,
                        err,
                    );
                }

                const tokenCacheForParsingThisTx = cashtabCache.tokens;
                let thisTokenCachedInfo;
                let tokenId;
                if (
                    incomingTxDetails.tokenStatus !==
                        'TOKEN_STATUS_NON_TOKEN' &&
                    incomingTxDetails.tokenEntries.length > 0
                ) {
                    // If this is a token tx with at least one tokenId that is NOT cached, get token info
                    // TODO we must get token info for multiple token IDs when we start supporting
                    // token types other than slpv1
                    tokenId = incomingTxDetails.tokenEntries[0].tokenId;
                    thisTokenCachedInfo = cashtabCache.tokens.get(tokenId);
                    if (typeof thisTokenCachedInfo === 'undefined') {
                        // If we do not have this token cached
                        // Note we do not update the cache here because this is handled in update
                        try {
                            thisTokenCachedInfo = await getTokenGenesisInfo(
                                chronik,
                                tokenId,
                            );
                            tokenCacheForParsingThisTx.set(
                                tokenId,
                                thisTokenCachedInfo,
                            );
                        } catch (err) {
                            console.error(
                                `Error fetching chronik.token(${tokenId})`,
                                err,
                            );

                            // Do not throw, in this case tokenCacheForParsingThisTx will still not
                            // include this token info, and the tx will be parsed as if it has 0 decimals

                            // We do not show the (wrong) amount in the notification if this is the case
                        }
                    }
                }

                // parse tx for notification
                // Include all HD receive/change hashes so change-address activity classifies correctly
                const walletHashes = ecashWalletRef.current
                    ? getWalletHashesForParseTx(ecashWalletRef.current)
                    : [];
                const parsedTx = parseTx(incomingTxDetails, walletHashes);

                // Add the new transaction to the beginning of the first page history
                setTransactionHistory(prev => {
                    if (!prev) {
                        return prev;
                    }

                    // Already present (e.g. race with refreshTransactionHistory)
                    if (prev.firstPageTxs.some(tx => tx.txid === txid)) {
                        return prev;
                    }

                    // Create the new transaction object
                    const newTx: CashtabTx = {
                        ...incomingTxDetails,
                        parsed: parsedTx,
                    };

                    // Add to beginning of first page
                    const updatedFirstPageTxs = [newTx, ...prev.firstPageTxs];

                    // If we're at the page limit, remove the last transaction
                    if (
                        updatedFirstPageTxs.length >
                        chronikConfig.txHistoryPageSize
                    ) {
                        updatedFirstPageTxs.pop();
                    }

                    // Calculate new counts
                    const newNumTxs = prev.numTxs + 1;
                    const newNumPages = Math.ceil(
                        newNumTxs / chronikConfig.txHistoryPageSize,
                    );

                    return {
                        ...prev,
                        firstPageTxs: updatedFirstPageTxs,
                        numTxs: newNumTxs,
                        numPages: newNumPages,
                    };
                });

                // Instant XECX/FIRMA redeems show a dedicated progress toast;
                // skip the generic "Sold ..." websocket notification.
                if (
                    parsedTx.parsedTokenEntries[0]?.renderedTxType ===
                        ParsedTokenTxType.AgoraSale &&
                    shouldSuppressInstantRedeemSaleToast(incomingTxDetails)
                ) {
                    return true;
                }

                // AlpSwap UI already toasted from→to for this settle.
                // In-flight covers the race where WS arrives before settle()
                // returns the txid (parse often misses alpSwap → "Sent …").
                if (
                    parsedTx.alpSwap?.role === 'buyer' ||
                    consumeAlpSwapSettleTxid(txid) ||
                    (isAlpSwapBuyerToastSuppressed() &&
                        parsedTx.parsedTokenEntries.length > 0)
                ) {
                    return true;
                }

                // parse tx for notification msg
                const notificationMsg = getTxNotificationMsg(
                    parsedTx,
                    fiatPrice,
                    locale,
                    settings.fiatCurrency.toUpperCase(),
                    thisTokenCachedInfo?.genesisInfo,
                );

                if (typeof notificationMsg === 'undefined') {
                    // We do not send a notification for some msgs
                    return;
                }

                // eToken txs should have token icon
                if (parsedTx.parsedTokenEntries.length > 0) {
                    toast(notificationMsg, {
                        icon: React.createElement(TokenIconToast, {
                            type: 'default',
                            theme: 'default',
                            size: 32,
                            tokenId: tokenId as string,
                        }),
                    });
                } else {
                    // Otherwise normal
                    toast(notificationMsg, {
                        icon: CashReceivedNotificationIcon,
                    });
                }
                return true;
            }
            case 'BLK_FINALIZED': {
                // Handle avalanche finalized block
                // NB we use BLK_FINALIZED msgs to set tipHeight, which is used for determining maturity
                // of Coinbase utxos (necessary to avoid errors trying to spend staking rewards with
                // less than 100 confirmations)
                return;
            }
            case 'TX_FINALIZED': {
                // Update tx if it is in the first page of history
                const txid = (msg as MsgTxClient).txid;

                // Drop dedupe entry so the Set does not grow without bound
                // across mined txs (HD multi-address can see many unique txids).
                processedMempoolTxids.current.delete(txid);

                // Use functional update to avoid race conditions
                // Mark every matching row final (legacy duplicate rows from
                // multi-address WS before dedupe, if any remain).
                setTransactionHistory(prev => {
                    if (!prev || prev.firstPageTxs.length === 0) {
                        return prev;
                    }

                    let changed = false;
                    const updatedFirstPageTxs = prev.firstPageTxs.map(tx => {
                        if (tx.txid === txid && !tx.isFinal) {
                            changed = true;
                            return { ...tx, isFinal: true };
                        }
                        return tx;
                    });

                    if (!changed) {
                        return prev;
                    }

                    return { ...prev, firstPageTxs: updatedFirstPageTxs };
                });

                return;
            }
            case 'TX_REMOVED_FROM_MEMPOOL': {
                // Rare
                // But, when this happens, we better be sure we are not showing this in the history
                console.info(`Tx removed from mempool: ${msg.txid}`);

                // Allow the same txid to be processed again if it re-enters mempool
                processedMempoolTxids.current.delete(msg.txid);

                // Remove the transaction from the first page history if it exists
                setTransactionHistory(prev => {
                    if (!prev || prev.firstPageTxs.length === 0) {
                        return prev;
                    }

                    const txid = msg.txid;
                    const txIndex = prev.firstPageTxs.findIndex(
                        tx => tx.txid === txid,
                    );

                    if (txIndex === -1) {
                        // Transaction not in first page, no update needed
                        return prev;
                    }

                    // Remove the transaction from first page
                    const updatedFirstPageTxs = prev.firstPageTxs.filter(
                        tx => tx.txid !== txid,
                    );

                    // NB we do not attempt to pull in the "next" tx from the next page, we can live
                    // with missing a tx in this exceptionally rare case, we just want to be sure
                    // we are not showing the user a dropped tx

                    // Calculate new counts
                    const newNumTxs = Math.max(0, prev.numTxs - 1);
                    const newNumPages = Math.max(
                        1,
                        Math.ceil(newNumTxs / chronikConfig.txHistoryPageSize),
                    ); // At least 1 page

                    return {
                        ...prev,
                        firstPageTxs: updatedFirstPageTxs,
                        numTxs: newNumTxs,
                        numPages: newNumPages,
                    };
                });

                // Refresh cashtab state
                await update();
                return;
            }
            default: {
                // Do nothing for other msg types
                return;
            }
        }
    };

    // We handle ws msgs by adding them to a queue and processing them sequentially
    const wsMessageHandler = async (msg: WsMsgClient) => {
        // Add to queue
        messageQueue.current.push(msg);

        // Process queue if not already processing
        if (!isProcessing.current) {
            await processMessageQueue();
        }
    };

    /**
     * Initialize Wallet from ecash-wallet when active wallet changes
     * Uses activeWalletAddress from cashtabState to find the active wallet
     * Adds name property to make ecashWallet the sole source of truth for all wallet info
     */
    const initializeWallet = async () => {
        const addressToUse = currentCashtabStateRef.current.activeWalletAddress;
        if (!addressToUse) {
            setEcashWallet(null);
            return;
        }

        // Find the active wallet from wallets array
        const activeWallet = currentCashtabStateRef.current.wallets.find(
            wallet => wallet.address === addressToUse,
        );
        if (!activeWallet) {
            setEcashWallet(null);
            return;
        }

        let newWallet: Wallet;
        if (isStoredHdWallet(activeWallet)) {
            // HD: restore from mnemonic with persisted next-unused indices
            newWallet = Wallet.fromMnemonic(activeWallet.mnemonic, chronik, {
                hd: true,
                accountNumber: activeWallet.accountNumber ?? 0,
                receiveIndex: activeWallet.receiveIndex ?? 0,
                changeIndex: activeWallet.changeIndex ?? 0,
                prefix: appConfig.prefix,
            });
        } else {
            // Single-address: sk at m/44'/1899'/0'/0/0 is enough
            const sk = fromHex(activeWallet.sk);
            newWallet = Wallet.fromSk(sk, chronik, {
                prefix: appConfig.prefix,
            });
        }
        ecashWalletRef.current = newWallet;
        // Sync on init (so we sync on app startup and on wallet switch)
        // NB we do not sync before sending txs, as ecash-wallet auto updates utxo sets on sending txs,
        // and we call "update" to sync when txs are received

        try {
            // HD: gap-limit discovery so receive/change indices track used addresses.
            // Start from persisted indices to avoid re-scanning from zero on every load.
            if (newWallet.isHD) {
                await newWallet.syncAndDiscoverAddresses({
                    startReceiveIndex: newWallet.receiveIndex,
                    startChangeIndex: newWallet.changeIndex,
                });
            } else {
                await newWallet.sync();
            }
            // Update state after sync so components re-render with updated wallet
            setEcashWallet(newWallet);

            // Generate tokens map from ecashWallet.utxos
            // This ensures tokens are available before cashtabLoaded is set to true
            // Note: generateTokensFromWalletUtxos may mutate cashtabCache.tokens (via getTokenBalances)
            const walletUtxos = newWallet.utxos;
            const tokens = await generateTokensFromWalletUtxos(
                chronik,
                walletUtxos,
                currentCashtabStateRef.current.cashtabCache.tokens,
            );

            // Update tokens in cashtabState
            // cashtabCache.tokens may have been mutated by generateTokensFromWalletUtxos,
            // so we need to update the cache in state to persist those changes
            await updateCashtabState({
                tokens: tokens as Map<string, string>,
                cashtabCache: currentCashtabStateRef.current.cashtabCache,
            } as Parameters<UpdateCashtabState>[0]);

            // Persist HD indices quietly after sync (no-op for single-address)
            await persistHdWalletIndices();
        } catch (error) {
            console.error(`Error in initializeWallet()`, error);
            // Set this in state so that transactions are disabled until the issue is resolved
            setApiError(true);
            // Set loading false, as we may not have set it to false by updating the wallet
            setLoading(false);
        }
    };

    /**
     * Write HD receive/change indices back to that wallet's record when they
     * move forward. Does not rewrite the wallet list.
     */
    const refreshWalletsFromStorage = async () => {
        const wallets = await readWalletRecords(storage);
        currentCashtabStateRef.current = {
            ...currentCashtabStateRef.current,
            wallets,
        };
        setCashtabState(prev => ({ ...prev, wallets }));
    };

    const persistHdWalletIndices = async () => {
        const liveWallet = ecashWalletRef.current;
        if (!liveWallet || !liveWallet.isHD) {
            return;
        }

        try {
            await enqueueWalletWrite(async () => {
                const updated = await persistHdIndexes(
                    storage,
                    liveWallet.address,
                    liveWallet.receiveIndex,
                    liveWallet.changeIndex,
                );
                if (updated === null) {
                    return;
                }
                await refreshWalletsFromStorage();
            });
        } catch (error) {
            console.error('Failed to persist HD wallet indexes', error);
        }
    };

    /**
     * For users opening the app in the extension or a webapp window,
     * the only thing that must be up-to-date is the utxo set; we do not
     * care about the token balances or tx history
     *
     * Speed and accurate utxo set are critical
     *
     * So, we load the utxo set first and unlock the UI
     *
     * Then we lazy load everything else
     */
    const startupUtxoSync = async () => {
        // Wallet is already initialized in loadCashtabState(), so we just need to sync and update
        if (ecashWalletRef.current === null) {
            return;
        }

        // We clear this flag even if we fail to get the latest utxo set
        // as we anticipate update() will catch the same API error
        setInitialUtxoSyncComplete(true);

        // Call the full update (this will sync the wallet and update tokens)
        update();
    };

    const update = async () => {
        if (!currentCashtabLoadedRef.current) {
            // Wait for cashtab to get state from storage before updating
            return;
        }

        // Initialize Wallet if not already initialized (this syncs the wallet)
        if (ecashWalletRef.current === null) {
            // In practice we do not expect to get to update with an un-initialized wallet
            await initializeWallet();
        }

        if (ecashWalletRef.current === null) {
            return;
        }

        try {
            // Sync wallet to get latest UTXOs
            // HD: rediscover from current indices so receive advances to the
            // next unused address after funds arrive.
            if (ecashWalletRef.current.isHD) {
                const prevReceive = ecashWalletRef.current.receiveIndex;
                const prevChange = ecashWalletRef.current.changeIndex;
                await ecashWalletRef.current.syncAndDiscoverAddresses({
                    startReceiveIndex: ecashWalletRef.current.receiveIndex,
                    startChangeIndex: ecashWalletRef.current.changeIndex,
                });
                // Clone when indices move so Receive / header re-render
                if (
                    prevReceive !== ecashWalletRef.current.receiveIndex ||
                    prevChange !== ecashWalletRef.current.changeIndex
                ) {
                    setEcashWallet(ecashWalletRef.current.clone());
                } else {
                    setEcashWallet(ecashWalletRef.current);
                }
            } else {
                await ecashWalletRef.current.sync();
                setEcashWallet(ecashWalletRef.current);
            }

            // Get UTXOs from the synced wallet
            const walletUtxos = ecashWalletRef.current.utxos;

            // Generate tokens map from ecashWallet.utxos
            // Note: this function will also update cashtabCache.tokens if any tokens are not in cache
            const tokens = await generateTokensFromWalletUtxos(
                chronik,
                walletUtxos,
                currentCashtabStateRef.current.cashtabCache.tokens,
            );

            // Update cashtabCache.tokens in state and storage (getTokenBalances may have updated it)
            updateCashtabState({
                cashtabCache: {
                    ...currentCashtabStateRef.current.cashtabCache,
                    tokens: currentCashtabStateRef.current.cashtabCache.tokens,
                },
                tokens,
            });

            // After sync / spend, HD indices may have advanced (change address)
            await persistHdWalletIndices();

            // If everything executed correctly, remove apiError
            setApiError(false);
        } catch (error) {
            console.error(`Error in update()`, error);
            // Set this in state so that transactions are disabled until the issue is resolved
            setApiError(true);
            // Set loading false, as we may not have set it to false by updating the wallet
            setLoading(false);
        }
    };

    /**
     * Lock UI while you update cashtabState in state and indexedDb
     * @param string
     * @param value what is being stored at this key
     */
    const updateCashtabState = async (updates: {
        [key: string]:
            | CashtabCache
            | CashtabContact[]
            | CashtabSettings
            | CashtabCacheJson
            | StoredCashtabWallet[]
            | Map<string, string>
            | string
            | null;
    }) => {
        for (const key of Object.keys(updates)) {
            if (
                key === LEGACY_WALLETS_STORAGE_KEY ||
                key.startsWith(WALLET_RECORD_PREFIX)
            ) {
                throw new Error(
                    'Wallet records must be saved with saveWallet or deleteWallet',
                );
            }
        }

        // Update all keys in state atomically
        setCashtabState(prevState => ({ ...prevState, ...updates }));

        // We lock the UI by setting loading to true while we set items in storage
        // This is to prevent rapid user action from corrupting the db
        setLoading(true);

        // Process each key for storage
        for (const [key, value] of Object.entries(updates)) {
            let storageKey = key;
            let storageValue = value;

            // Handle tokens map - convert to array for storage
            if (key === 'tokens') {
                storageKey = 'tokens';
                // Convert Map to array of [key, value] pairs for JSON storage
                storageValue = Array.from(value as Map<string, string>);
            }

            // Handle any items that must be converted to JSON before storage
            // For now, this is just cashtabCache
            if (storageKey === 'cashtabCache') {
                storageValue = cashtabCacheToJSON(value as CashtabCache);
            }

            await storage.set(storageKey, storageValue);
        }

        setLoading(false);
        return true;
    };

    /**
     * Load all keys from storage into state
     *
     * If any are invalid, migrate them to valid and update in storage
     *
     * We only do this when the user starts Cashtab
     *
     * While the app is running, we use cashtabState as the source of truth
     *
     * We save to storage on state changes in updateCashtabState
     * so that these persist if the user navigates away from Cashtab     *
     */
    const loadCashtabState = async () => {
        // cashtabState is initialized with defaults when this component loads

        // One bulk read, instead of a key-by-key scan, on startup.
        const snapshot = new Map(await storage.entries());
        const stored = <T>(key: string): T | null =>
            snapshot.has(key) ? (snapshot.get(key) as T) : null;

        // contactList
        let contactList: null | CashtabContact[] =
            stored<CashtabContact[]>('contactList');
        if (contactList !== null) {
            // If we find a contactList in storage
            if (!isValidContactList(contactList)) {
                // and this contactList is invalid, migrate

                // contactList is only expected to be invalid as legacy empty, i.e. [{}]
                // We do not call a function to migrate contactList as no other migration is expected
                contactList = [];
                // Update storage on app load only if existing values are in an obsolete format
                updateCashtabState({
                    contactList: contactList as CashtabContact[],
                });
            }
            // Set cashtabState contactList to valid storage or migrated
            cashtabState.contactList = contactList as CashtabContact[];
        }

        // settings
        let settings: null | CashtabSettings =
            stored<CashtabSettings>('settings');
        if (settings !== null) {
            // If we find settings in storage
            if (!isValidCashtabSettings(settings)) {
                // If a settings object is present but invalid, parse to find and add missing keys
                settings = migrateLegacyCashtabSettings(
                    settings as unknown as CashtabSettings,
                );
                // Update storage on app load only if existing values are in an obsolete format
                updateCashtabState({
                    settings: settings as unknown as CashtabSettings,
                });
            }

            // Set cashtabState settings to valid storage or migrated settings
            cashtabState.settings = settings as CashtabSettings;
        }

        // cashtabCache
        let cashtabCache: null | CashtabCacheJson | CashtabCache = stored<
            CashtabCacheJson | CashtabCache
        >('cashtabCache');

        if (cashtabCache !== null) {
            // If we find cashtabCache in storage

            // cashtabCache must be converted from JSON as it stores a Map
            cashtabCache = storedCashtabCacheToMap(
                cashtabCache as CashtabCacheJson,
            );

            if (!isValidCashtabCache(cashtabCache)) {
                // If a cashtabCache object is present but invalid, nuke it and start again
                cashtabCache = cashtabState.cashtabCache;
                // Update storage on app load only if existing values are in an obsolete format
                updateCashtabState({ cashtabCache: cashtabCache });
            }

            // Set cashtabState cashtabCache to valid storage or migrated settings
            cashtabState.cashtabCache = cashtabCache;
        }

        // Load wallets if present. Legacy installs store one `wallets` array;
        // migrate that into one record per address before reading.

        const wallets = await migrateLegacyWallets(storage, snapshot);
        const activeWalletAddress = stored<string>('activeWalletAddress');
        // Load tokens from storage (if exists)
        const storedTokens: null | [string, string][] =
            stored<[string, string][]>('tokens');
        if (storedTokens !== null) {
            cashtabState.tokens = new Map(storedTokens);
        }

        if (wallets.length > 0) {
            // Returning user. A missing active wallet opens the first by name
            // instead of leaving the app on the loading screen.
            const resolvedActive = resolveActiveWalletAddress(
                wallets,
                activeWalletAddress,
            );
            if (resolvedActive !== activeWalletAddress) {
                try {
                    await storage.set('activeWalletAddress', resolvedActive);
                } catch (err) {
                    console.error('Failed to persist active wallet', err);
                }
            }
            cashtabState.wallets = wallets;
            cashtabState.activeWalletAddress = resolvedActive;
        } else if (activeWalletAddress === null) {
            // New user
            console.info(
                `No wallets found in storage, initializing for new user`,
            );
            setInitialUtxoSyncComplete(true);
            cashtabState.wallets = [];
        } else {
            // Corrupted storage (partial data)
            toast.error(
                'Corrupted storage: Cashtab was unable to load wallets from storage',
            );
            cashtabState.wallets = [];
        }

        setCashtabState(cashtabState);
        // Update ref immediately so initializeWallet() can use the latest state
        currentCashtabStateRef.current = cashtabState;

        // Initialize wallet if we have an activeWalletAddress, then mark as loaded
        if (cashtabState.activeWalletAddress) {
            // Existing user - initialize wallet before marking as loaded
            await initializeWallet();
        } else {
            // New user - no wallet to initialize
            setEcashWallet(null);
        }

        setCashtabLoaded(true);

        // Initialize the websocket connection

        // Initialize onMessage with loaded wallet. fiatPrice may be null.
        // The onMessage routine will update when active wallet or fiatPrice updates
        const ws = chronik.ws({
            onReconnect: e => {
                // Fired before a reconnect attempt is made.
                // Should never happen with chronik ping keepalive websocket
                console.info(
                    'Reconnecting websocket, disconnection cause: ',
                    e,
                );
            },
            onConnect: e => {
                console.info(`Chronik websocket connected`, e);
            },
        });

        // Wait for websocket to be connected:
        await ws.waitForOpen();

        // We always subscribe to blocks
        ws.subscribeToBlocks();

        // Subscribe to wallet address if wallet was initialized above
        if (cashtabState.activeWalletAddress && ecashWalletRef.current) {
            // Subscribe to all known HD addresses (or the single address)
            for (const address of getWalletAddressesForSubscription(
                ecashWalletRef.current,
            )) {
                ws.subscribeToAddress(address);
            }
        } else {
            // Set loading to false if we have no wallet
            // as we will not get to the update() until the user creates a wallet
            setLoading(false);
        }
        // When the user creates or imports a wallet, ws subscriptions will be handled by updateWebsocket

        // Put connected websocket in state
        setWs(ws);
    };

    /**
     * Update websocket subscriptions when active wallet changes
     * Update websocket onMessage handler when fiatPrice changes
     */
    const updateWebsocket = () => {
        if (ws === null) {
            // Should never happen, we only call this in a useEffect when ws is not null
            return;
        }
        if (!ecashWalletRef.current) {
            // Should never happen, we only call this in a useEffect when ecashWallet is defined
            return;
        }
        // Set or update the onMessage handler
        // We can only set this when wallet is defined, so we do not set it in loadCashtabState
        ws.onMessage = wsMessageHandler;

        // Check if current subscriptions match current wallet
        const { subs } = ws;
        // scripts may be undefined in mocked-chronik-client, as it continues to support nng chronik-client
        const scripts = 'scripts' in subs ? ws.subs.scripts : [];

        // Get the subscribed payloads so we can iterate over these to unsub
        // If we iterate over ws.subs to unsubscribe, we are modifying ws.subs as we iterate
        // Can lead to off-by-one and other errors
        const subscribedPayloads = [];
        for (const script of scripts) {
            subscribedPayloads.push(script.payload);
        }

        const wantedAddresses = getWalletAddressesForSubscription(
            ecashWalletRef.current,
        );
        const wantedPayloads = new Set(
            getWalletHashesForParseTx(ecashWalletRef.current),
        );

        const subscribedSet = new Set(subscribedPayloads);
        const subscriptionsMatch =
            subscribedSet.size === wantedPayloads.size &&
            [...wantedPayloads].every(payload => subscribedSet.has(payload));

        if (!subscriptionsMatch) {
            // Unsubscribe from all existing subscriptions
            for (const payload of subscribedPayloads) {
                ws.unsubscribeFromScript('p2pkh', payload);
            }

            // Subscribe to every known wallet address (receive + change for HD)
            for (const address of wantedAddresses) {
                ws.subscribeToAddress(address);
            }
        }

        // Update ws in state
        return setWs(ws);
    };

    // With different currency selections possible, need unique intervals for price checks
    // Must be able to end them and set new ones with new currencies
    const initializeFiatPriceApi = async (selectedFiatCurrency: string) => {
        if (import.meta.env.VITE_TESTNET === 'true') {
            setFiatPrice(0);
            setFirmaPrice(0);
            return;
        }
        // Update fiat price and confirm it is set to make sure ap keeps loading state until this is updated

        // Call this instance with showNotifications = false,
        // as we do not want to calculate price deltas when the user selects a new foreign currency
        await fetchXecPrice(selectedFiatCurrency);

        if (selectedFiatCurrency === 'usd') {
            setFirmaPrice(1);
        } else {
            await fetchFirmaPrice(selectedFiatCurrency);
        }
        // Set interval for updating the price with given currency

        // Now we call with showNotifications = true, as we want
        // to show price changes when the currency has not changed
        const thisFiatInterval = setInterval(function () {
            fetchXecPrice(selectedFiatCurrency);
            if (selectedFiatCurrency !== 'usd') {
                fetchFirmaPrice(selectedFiatCurrency);
            }
        }, appConfig.fiatUpdateIntervalMs);

        // set interval in state
        setCheckFiatInterval(thisFiatInterval);
    };

    const clearFiatPriceApi = (fiatPriceApi: NodeJS.Timeout) => {
        // Clear fiat price check interval of previously selected currency
        clearInterval(fiatPriceApi);
    };

    const fetchXecPrice = async (
        fiatCode = typeof cashtabState?.settings?.fiatCurrency !== 'undefined'
            ? cashtabState.settings.fiatCurrency
            : 'usd',
    ) => {
        // Split this variable out in case coingecko changes
        const cryptoId = appConfig.coingeckoId;
        // Keep this in the code, because different URLs will have different outputs require different parsing
        const priceApiUrl = `https://api.coingecko.com/api/v3/simple/price?ids=${cryptoId}&vs_currencies=${fiatCode}&include_last_updated_at=true`;
        try {
            const xecPrice = await fetch(priceApiUrl);
            if (xecPrice.ok === false) {
                // Most common case is CoinGecko 429; avoid parsing an error body as JSON
                console.error(
                    `Failed to fetch XEC Price: Bad response or rate limit from CoinGecko`,
                );
                return setFiatPrice(null);
            }
            const xecPriceJson = await xecPrice.json();
            const xecPriceInFiat = xecPriceJson[cryptoId][fiatCode];

            if (typeof xecPriceInFiat === 'number') {
                // If we have a good fetch
                return setFiatPrice(xecPriceInFiat);
            }
        } catch (err) {
            if (
                typeof err === 'object' &&
                err !== null &&
                'message' in err &&
                err.message === 'Failed to fetch'
            ) {
                // The most common error is coingecko 429
                console.error(
                    `Failed to fetch XEC Price: Bad response or rate limit from CoinGecko`,
                );
            } else {
                console.error(`Failed to fetch XEC Price`, err);
            }
        }
        // If we have an error in the price fetch, or an invalid type without one, do not set the price
        return setFiatPrice(null);
    };

    /**
     * If the user does not have USD as fiat currency,
     * we need to get an exchange rate for Firma (USD-pegged).
     *
     * Use CoinGecko exchange_rates (BTC-indexed fiat table). Do NOT use
     * simple/price?ids=usd — that id is an unrelated crypto token, not the
     * US dollar, and returns nonsense forex (e.g. ~0.11 INR per "usd").
     */
    const fetchFirmaPrice = async (
        fiatCode = cashtabState.settings.fiatCurrency,
    ) => {
        // Keep this in the code, because different URLs will have different outputs require different parsing
        const priceApiUrl = 'https://api.coingecko.com/api/v3/exchange_rates';
        try {
            const firmaPrice = await fetch(priceApiUrl);
            if (firmaPrice.ok === false) {
                // Most common case is CoinGecko 429; avoid parsing an error body as JSON
                console.error(
                    `Failed to fetch Firma Price: Bad response or rate limit from CoinGecko`,
                );
                return setFirmaPrice(null);
            }
            const firmaPriceJson = await firmaPrice.json();
            const rates = firmaPriceJson?.rates;
            const usdRate = rates?.usd?.value;
            const fiatRate = rates?.[fiatCode]?.value;

            if (
                Number.isFinite(usdRate) &&
                usdRate > 0 &&
                Number.isFinite(fiatRate) &&
                fiatRate > 0
            ) {
                // Rates are BTC-indexed; USD→fiat is the ratio of fiat/usd values
                return setFirmaPrice(fiatRate / usdRate);
            }
        } catch (err) {
            if (
                typeof err === 'object' &&
                err !== null &&
                'message' in err &&
                err.message === 'Failed to fetch'
            ) {
                // The most common error is coingecko 429
                console.error(
                    `Failed to fetch Firma Price: Bad response or rate limit from CoinGecko`,
                );
            } else {
                console.error(`Failed to fetch Firma Price`, err);
            }
        }
        // If we have an error in the price fetch, or an invalid type without one, do not set the price
        return setFirmaPrice(null);
    };

    /**
     * Fetch average Firma APY from FIRMA_APY_API_URL (AVG of past
     * daily apy_spot payouts).
     * APY updates once per day; call only once on app load.
     */
    const fetchFirmaApy = async () => {
        try {
            const response = await fetch(FIRMA_APY_API_URL);
            if (response.ok === false) {
                console.error(
                    `Failed to fetch Firma APY: Bad response from stakedxec.com`,
                );
                return setFirmaApy(null);
            }
            const apyJson = await response.json();
            const averageApy = apyJson.average_apy_spot;
            if (typeof averageApy === 'number' && Number.isFinite(averageApy)) {
                return setFirmaApy(averageApy);
            }
        } catch (err) {
            console.error(`Failed to fetch Firma APY`, err);
        }
        return setFirmaApy(null);
    };

    /**
     * Fetch XECX APY from XECX_APY_API_URL (same endpoint as the marketing
     * site hero). The API returns a decimal fraction; we store percent.
     * APY updates once per day; call only once on app load.
     */
    const fetchXecxApy = async () => {
        try {
            const response = await fetch(XECX_APY_API_URL);
            if (response.ok === false) {
                console.error(
                    `Failed to fetch XECX APY: Bad response from ${XECX_APY_API_URL}`,
                );
                return setXecxApy(null);
            }
            const apyJson = await response.json();
            const apyFraction = apyJson.apy;
            if (
                typeof apyFraction === 'number' &&
                Number.isFinite(apyFraction)
            ) {
                // Hero displays 100 * apy with 2 decimals.
                return setXecxApy(100 * apyFraction);
            }
        } catch (err) {
            console.error(`Failed to fetch XECX APY`, err);
        }
        return setXecxApy(null);
    };

    const cashtabBootup = async () => {
        // Initialize platform storage
        const storageInit = await initializeStorage();
        if (!storageInit.success) {
            console.error('Failed to initialize storage:', storageInit.error);
            // We continue anyway as storage adapters have fallbacks
        }

        await loadCashtabState();
    };

    useEffect(() => {
        cashtabBootup();
    }, []);
    // Initialize Wallet when activeWalletAddress in cashtabState changes or wallets are added
    // (only after initial load - initial load is handled in loadCashtabState)
    // Note: cashtabLoaded is NOT in the dependency array to avoid redundant initialization during startup
    useEffect(() => {
        if (cashtabLoaded) {
            initializeWallet();
        }
    }, [cashtabState.wallets.length, cashtabState.activeWalletAddress]);

    // Call the update loop every time the user changes the active wallet
    // and immediately after cashtab is loaded
    useEffect(() => {
        if (cashtabLoaded) {
            // Sync utxos to unlock the UI, and then lazy load the rest of Cashtab state
            startupUtxoSync();
        }
    }, [cashtabLoaded]);

    // Clear price API and update to new price API when fiat currency changes
    useEffect(() => {
        if (cashtabLoaded !== true) {
            // Wait for Cashtab to load the user's fiat currency from saved settings before starting the price API
            return;
        }
        // Clear existing fiat price API check
        if (checkFiatInterval !== null) {
            clearFiatPriceApi(checkFiatInterval);
        }

        // Reset fiat price API when fiatCurrency setting changes
        initializeFiatPriceApi(cashtabState.settings.fiatCurrency);
    }, [cashtabLoaded, cashtabState.settings.fiatCurrency]);

    // Firma and XECX APY update once per day — fetch once on app load only
    // (not on fiat-currency re-init or the price interval).
    useEffect(() => {
        if (cashtabLoaded !== true) {
            return;
        }
        if (import.meta.env.VITE_TESTNET === 'true') {
            setFirmaApy(null);
            setXecxApy(null);
            return;
        }
        fetchFirmaApy();
        fetchXecxApy();
    }, [cashtabLoaded]);

    /**
     * useEffect
     * Depends on fiatPrice and user-set fiatCurrency
     * Used to trigger price notifications at new fiatPrice milestones
     * Optimized for USD
     * Also supports EUR and GBP as these are "close enough", for now anyway
     */
    useEffect(() => {
        // Do nothing if the user has just changed the fiat currency
        if (cashtabState.settings.fiatCurrency !== prevFiatCurrency) {
            return;
        }

        // We only support currencies that are similar order of magnitude to USD
        // USD is the real referencce for "killed zero"
        const FIAT_CHANGE_SUPPORTED_CURRENCIES = [
            'usd',
            'eur',
            'gbp',
            'cad',
            'aud',
        ];
        if (
            !FIAT_CHANGE_SUPPORTED_CURRENCIES.includes(
                cashtabState.settings.fiatCurrency,
            )
        ) {
            return;
        }
        // Otherwise we do support them
        if (
            fiatPrice === null ||
            prevFiatPrice === null ||
            typeof prevFiatPrice === 'undefined'
        ) {
            return;
        }
        const priceIncreased = fiatPrice - prevFiatPrice > 0;
        if (priceIncreased) {
            // We only show price notifications if price has increased
            // "tens" for USD price per 1,000,000 XEC
            const prevTens = Math.floor(prevFiatPrice * 1e5);
            const tens = Math.floor(fiatPrice * 1e5);
            if (tens > prevTens) {
                // We have passed a $10 milestone
                toast(
                    `XEC is now ${
                        supportedFiatCurrencies[
                            cashtabState.settings.fiatCurrency
                        ].symbol
                    }${fiatPrice} ${cashtabState.settings.fiatCurrency.toUpperCase()}`,
                    { icon: CashReceivedNotificationIcon },
                );
            }
            if (tens >= 10 && prevTens < 10) {
                // We have killed a zero
                toast(`ZERO KILLED 🔫🔫🔫🔪🔪🔪`, {
                    autoClose: false,
                    icon: CashReceivedNotificationIcon,
                });
            }
        }
    }, [fiatPrice, cashtabState.settings.fiatCurrency]);

    // Update websocket subscriptions and websocket onMessage handler whenever
    // 1. cashtabState changes
    // 2. or the fiat price updates (the onMessage handler needs to have the most up-to-date
    // fiat price)
    // This is because the onMessage routine only has access to the state variables when onMessage was set
    // and the update() function needs the most recent cashtabState to update cashtabState
    useEffect(() => {
        if (
            cashtabLoaded !== true ||
            ws === null ||
            typeof cashtabState.wallets[0] === 'undefined'
        ) {
            // Only update the websocket if
            // 1. ws is not null
            // 2. Cashtab has loaded
            // 3. We have an active wallet
            // 4. fiatPrice has changed
            // We can call with fiatPrice of null, we will not always have fiatPrice
            return;
        }
        updateWebsocket();
    }, [cashtabState, fiatPrice, ws, cashtabLoaded]);

    // Handle app lifecycle events to keep websocket alive on mobile
    useEffect(() => {
        // Only set up app lifecycle listeners on native platforms
        if (Capacitor.isNativePlatform()) {
            console.log(
                `Setting up app lifecycle listeners on native platform`,
            );
            let appStateListener: { remove: () => void } | null = null;

            const setupAppListeners = async () => {
                // Listen for app state changes (foreground/background)
                appStateListener = await App.addListener(
                    'appStateChange',
                    state => {
                        if (state.isActive) {
                            // App came to foreground - update state and resume websocket
                            isAppActiveRef.current = true;
                            console.log(
                                'App came to foreground, resuming websocket...',
                            );
                            const currentWs = wsRef.current;
                            if (currentWs) {
                                currentWs.resume();
                            }
                        } else {
                            // App went to background - pause websocket to save resources
                            isAppActiveRef.current = false;
                            const currentWs = wsRef.current;
                            if (currentWs) {
                                currentWs.pause();
                                console.log(
                                    'App went to background, paused websocket',
                                );
                            } else {
                                console.log('App went to background');
                            }
                        }
                    },
                );
            };

            setupAppListeners();

            return () => {
                if (appStateListener) {
                    appStateListener.remove();
                }
            };
        }
    }, []);

    /**
     * Handle activating a copied wallet by updating activeWalletAddress in CashtabState
     * This is used for address sharing scenarios where we don't need to fully initialize the wallet
     * @param walletAddress The address of the wallet to activate
     */

    /**
     * Get a StoredCashtabWallet by its address
     * @param address The wallet address to look up
     * @returns The StoredCashtabWallet if found, null otherwise
     */
    const getWalletByAddress = (
        address: string,
    ): StoredCashtabWallet | null => {
        // We might have existing storage for another prefix, so make sure to
        // convert properly
        return (
            currentCashtabStateRef.current.wallets.find(wallet => {
                try {
                    return (
                        Address.parse(wallet.address)
                            .withPrefix(appConfig.prefix)
                            .toString() === address
                    );
                } catch {
                    return false;
                }
            }) || null
        );
    };

    const saveWallet = async (
        wallet: StoredCashtabWallet,
        activeAddress?: string,
    ): Promise<void> => {
        setLoading(true);
        try {
            await enqueueWalletWrite(async () => {
                await saveWalletRecord(storage, wallet);
                if (typeof activeAddress === 'string') {
                    await storage.set('activeWalletAddress', activeAddress);
                }
                const wallets = await readWalletRecords(storage);
                const nextActive =
                    typeof activeAddress === 'string'
                        ? activeAddress
                        : currentCashtabStateRef.current.activeWalletAddress;
                currentCashtabStateRef.current = {
                    ...currentCashtabStateRef.current,
                    wallets,
                    activeWalletAddress: nextActive,
                };
                setCashtabState(prev => ({
                    ...prev,
                    wallets,
                    activeWalletAddress: nextActive,
                }));
            });
        } catch (error) {
            console.error('Failed to save wallet', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const deleteWallet = async (address: string): Promise<void> => {
        setLoading(true);
        try {
            await enqueueWalletWrite(async () => {
                await deleteWalletRecord(storage, address);
                const wallets = await readWalletRecords(storage);
                const active = resolveActiveWalletAddress(
                    wallets,
                    currentCashtabStateRef.current.activeWalletAddress,
                );
                if (
                    active !==
                    currentCashtabStateRef.current.activeWalletAddress
                ) {
                    await storage.set('activeWalletAddress', active);
                }
                currentCashtabStateRef.current = {
                    ...currentCashtabStateRef.current,
                    wallets,
                    activeWalletAddress: active,
                };
                setCashtabState(prev => ({
                    ...prev,
                    wallets,
                    activeWalletAddress: active,
                }));
            });
        } catch (error) {
            console.error('Failed to delete wallet', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    return {
        chronik,
        agora,
        ecc,
        fiatPrice,
        firmaPrice,
        firmaApy,
        xecxApy,
        cashtabLoaded,
        loading,
        setLoading,
        initialUtxoSyncComplete,
        apiError,
        updateCashtabState,
        processChronikWsMsg: async (msg: WsMsgClient) => {
            await wsMessageHandler(msg);
            return true;
        },
        cashtabState,
        transactionHistory,
        refreshTransactionHistory,
        update,
        setCashtabState,
        ecashWallet,
        getWalletByAddress,
        saveWallet,
        deleteWallet,
    } as UseWalletReturnType;
};

export default useWallet;
